import { useEffect, useState } from 'react';
import { Modal, Form, Button, Message, Select, TextArea, Checkbox } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { createMoveRequest, updateMoveRequest, getActiveTeams } from './assetMoveService';

export const MoveAssetModal = ({ iccName, currentTeamId, onSuccess, onClose, initialValues, existingId }) => {
    const [open, setOpen] = useState(true);
    const [moveDate, setMoveDate] = useState(() => {
        if (initialValues?.moveDate) return new Date(initialValues.moveDate + 'T00:00:00');
        return null;
    });
    const [targetTeam, setTargetTeam] = useState(
        initialValues?.targetTeam ?? null
    );
    const [notes, setNotes] = useState(initialValues?.notes ?? '');
    const [moveAssociatedAssets, setMoveAssociatedAssets] = useState(
        initialValues?.moveAssociatedAssets ?? true
    );
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isImmediate = moveDate && moveDate <= today;

    useEffect(() => {
        if (!open) return;
        getActiveTeams()
            .then(activeTeams => setTeams(activeTeams.filter(t => t.id !== currentTeamId)))
            .catch(() => setError('Failed to load teams.'));
    }, [open, currentTeamId]);

    function handleClose() {
        setOpen(false);
        onClose?.();
    }

    async function handleSubmit() {
        if (!targetTeam || !moveDate) {
            setError('Move date and destination team are required.');
            return;
        }
        setLoading(true);
        setError(null);
        const payload = {
            asset: iccName,
            moveDate: `${moveDate.getFullYear()}-${String(moveDate.getMonth() + 1).padStart(2, '0')}-${String(moveDate.getDate()).padStart(2, '0')}`,
            targetTeam: targetTeam,
            notes,
            moveAssociatedAssets,
        };
        try {
            const result = existingId
                ? await updateMoveRequest(existingId, payload)
                : await createMoveRequest(payload);
            setOpen(false);
            onSuccess(result);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    const teamOptions = teams.map(t => ({ key: t.id, value: t.id, text: t.name }));

    return (
        <Modal open={open} onClose={handleClose} size="small">
            <Modal.Header>{existingId ? 'Edit Move Request' : 'Schedule Asset Move'}</Modal.Header>
            <Modal.Content>
                {error && <Message negative content={error} />}
                {isImmediate && (
                    <Message
                        warning
                        header="Immediate move"
                        content="This move will be executed immediately because the move date is today. The asset's team will be updated right away."
                    />
                )}
                <Form>
                    <Form.Field required>
                        <label>Move Date</label>
                        <DatePicker
                            selected={moveDate}
                            onChange={(date) => setMoveDate(date || null)}
                            dateFormat="MM/dd/yyyy"
                            minDate={today}
                            isClearable
                            placeholderText="Select date"
                            className="ui input"
                            wrapperClassName="datepicker-wrapper"
                            popperPlacement="bottom-start"
                        />
                    </Form.Field>
                    <Form.Field
                        required
                        control={Select}
                        label="Destination Team"
                        options={teamOptions}
                        value={targetTeam}
                        onChange={(_e, { value }) => setTargetTeam(value)}
                        placeholder="Select a team"
                        disabled={!!existingId}
                    />
                    <Form.Field
                        control={TextArea}
                        label="Notes"
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="Optional notes"
                    />
                    <Form.Field>
                        <Checkbox
                            label="Move associated assets"
                            checked={moveAssociatedAssets}
                            value={moveAssociatedAssets}
                            onChange={(_e, { checked }) => setMoveAssociatedAssets(checked)}
                            disabled={!!existingId}
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button primary loading={loading} onClick={handleSubmit}>
                    {existingId ? 'Save Changes' : 'Schedule Move'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
